"use client";

/**
 * BankMock — animated mock of the askFinz Bank workspace for /apps/bank.
 *
 * Pure presentation: a 3-pane layout — account list (L) with three
 * connected accounts, a transaction feed (C) with merchant + category
 * chips, and a budget panel (R) with three category bars. A "transfer"
 * pill at the top fires mid-animation: $400 from Checking → Savings,
 * accounts and budget bars update.
 *
 * Trademark guardrail: no real bank or merchant names. Use placeholders
 * ("Cedar Bank · Checking", "Local market"). Lives in askfinz.com only.
 */
import { useEffect, useRef, useState } from "react";
import {
  Landmark,
  Wallet,
  ArrowUpRight,
  ArrowDownLeft,
  TrendingUp,
  Coffee,
  ShoppingBag,
  Utensils,
  Receipt,
  Send,
  Sparkles,
  ShieldCheck,
} from "lucide-react";

type Account = {
  id: string;
  name: string;
  kind: "Checking" | "Savings" | "Credit";
  balance: number;
};

const INITIAL_ACCOUNTS: Account[] = [
  { id: "a1", name: "Cedar Bank",   kind: "Checking", balance: 4280 },
  { id: "a2", name: "Cedar Bank",   kind: "Savings",  balance: 12450 },
  { id: "a3", name: "Maple Credit", kind: "Credit",   balance: -1180 },
];

type Tx = {
  id: string;
  merchant: string;
  category: string;
  icon: typeof Coffee;
  amount: number;
  account: string;
  when: string;
};

const INITIAL_TX: Tx[] = [
  { id: "t1", merchant: "Local market",       category: "Groceries", icon: ShoppingBag, amount: -64.20, account: "Checking",  when: "Today" },
  { id: "t2", merchant: "Salary · Apr",        category: "Income",    icon: TrendingUp,  amount: 4200,    account: "Checking",  when: "Yesterday" },
  { id: "t3", merchant: "Coffee shop",        category: "Dining",    icon: Coffee,      amount: -6.40,   account: "Checking",  when: "Yesterday" },
  { id: "t4", merchant: "Subscription · TV",  category: "Bills",     icon: Receipt,     amount: -14.99,  account: "Credit",    when: "2d" },
  { id: "t5", merchant: "Local diner",        category: "Dining",    icon: Utensils,    amount: -28.50,  account: "Checking",  when: "3d" },
];

const TRANSFER_TX: Tx = {
  id: "t-tr",
  merchant: "Transfer · to Savings",
  category: "Transfer",
  icon: ArrowDownLeft,
  amount: -400,
  account: "Checking",
  when: "Just now",
};

type Budget = {
  id: string;
  label: string;
  cap: number;
  spent: number;
  icon: typeof Coffee;
};

const INITIAL_BUDGETS: Budget[] = [
  { id: "b1", label: "Groceries", cap: 600, spent: 318, icon: ShoppingBag },
  { id: "b2", label: "Dining",    cap: 250, spent: 192, icon: Utensils },
  { id: "b3", label: "Subs",      cap: 80,  spent: 58,  icon: Receipt },
];

export function BankMock() {
  const [accounts, setAccounts] = useState<Account[]>(INITIAL_ACCOUNTS);
  const [tx, setTx] = useState<Tx[]>(INITIAL_TX);
  const [transferring, setTransferring] = useState(false);
  const [budgets] = useState<Budget[]>(INITIAL_BUDGETS);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const timers: number[] = [];
    const schedule = (fn: () => void, delay: number) => {
      timers.push(window.setTimeout(fn, delay));
    };

    const runCycle = () => {
      setAccounts(INITIAL_ACCOUNTS);
      setTx(INITIAL_TX);
      setTransferring(false);

      // 1. Show transfer pill firing
      schedule(() => setTransferring(true), 1000);

      // 2. Apply transfer to accounts + tx feed
      schedule(() => {
        setAccounts((acs) =>
          acs.map((a) =>
            a.kind === "Checking"
              ? { ...a, balance: a.balance - 400 }
              : a.kind === "Savings"
              ? { ...a, balance: a.balance + 400 }
              : a
          )
        );
        setTx((cur) => [TRANSFER_TX, ...cur]);
      }, 2200);

      // 3. Settle pill
      schedule(() => setTransferring(false), 3400);

      // Loop
      schedule(runCycle, 6800);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        runCycle();
        obs.disconnect();
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  const netWorth = accounts.reduce((s, a) => s + a.balance, 0);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            bank.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
            Demo
          </span>
        </div>

        {/* Summary strip */}
        <div className="px-4 py-3 border-b hairline bg-foreground/[0.02] flex items-center justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono">Net worth</div>
            <div className="font-mono text-[20px] tabular-num text-foreground/90 transition-all duration-500">
              ${netWorth.toLocaleString()}
            </div>
          </div>
          <div
            className={
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] transition-all " +
              (transferring
                ? "bg-[hsl(var(--accent))] text-background"
                : "bg-foreground text-background")
            }
          >
            <Send className={"h-3.5 w-3.5 " + (transferring ? "animate-pulse" : "")} />
            {transferring ? "Sending $400 · Checking → Savings" : "Move money"}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr_220px] gap-px bg-foreground/10 min-h-[360px]">
          {/* Account list */}
          <div className="bg-background p-3">
            <div className="flex items-center gap-1.5 mb-3 text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
              <Landmark className="h-3 w-3" />
              Accounts
            </div>
            <ul className="space-y-2">
              {accounts.map((a) => (
                <li
                  key={a.id}
                  className="rounded-md border hairline bg-foreground/[0.015] px-2.5 py-2 transition-all"
                >
                  <div className="flex items-center justify-between text-[10px] font-mono text-foreground/65 tracking-[0.12em] uppercase">
                    <span>{a.name}</span>
                    <span>{a.kind}</span>
                  </div>
                  <div
                    className={
                      "mt-1 font-mono text-[14px] tabular-num transition-all duration-500 " +
                      (a.balance < 0 ? "text-[hsl(var(--accent))]" : "text-foreground/85")
                    }
                  >
                    {a.balance < 0 ? "-" : ""}${Math.abs(a.balance).toLocaleString()}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Transaction feed */}
          <div className="bg-background p-3">
            <div className="flex items-center gap-1.5 mb-3 text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
              <Wallet className="h-3 w-3" />
              Recent activity
            </div>
            <ul className="space-y-1.5">
              {tx.slice(0, 6).map((t) => {
                const Icon = t.icon;
                const positive = t.amount > 0;
                return (
                  <li
                    key={t.id}
                    className="flex items-center gap-2.5 rounded-md border hairline bg-foreground/[0.015] px-2.5 py-1.5 animate-[blur-fade-in_0.4s_ease-out]"
                  >
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border hairline bg-background shrink-0">
                      <Icon className="h-3 w-3 text-foreground/65" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-foreground/85 truncate">{t.merchant}</div>
                      <div className="text-[10px] text-foreground/65 font-mono">
                        {t.category} · {t.account} · {t.when}
                      </div>
                    </div>
                    <div
                      className={
                        "font-mono text-[12px] tabular-num " +
                        (positive ? "text-[hsl(var(--accent))]" : "text-foreground/85")
                      }
                    >
                      {positive ? "+" : ""}${Math.abs(t.amount).toFixed(2)}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Budgets */}
          <div className="bg-background p-3">
            <div className="flex items-center gap-1.5 mb-3 text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
              <TrendingUp className="h-3 w-3" />
              This month
            </div>
            <ul className="space-y-3">
              {budgets.map((b) => {
                const Icon = b.icon;
                const pct = Math.min(100, (b.spent / b.cap) * 100);
                const hot = pct > 75;
                return (
                  <li key={b.id}>
                    <div className="flex items-center gap-1.5 text-[11px] mb-1">
                      <Icon className="h-3 w-3 text-foreground/65" />
                      <span className="text-foreground/75">{b.label}</span>
                      <span className="ml-auto font-mono text-[10px] text-foreground/65">
                        ${b.spent} / ${b.cap}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-foreground/10 overflow-hidden">
                      <div
                        className={"h-full transition-all duration-700 " + (hot ? "bg-[hsl(var(--accent))]" : "bg-foreground")}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="mt-4 pt-3 border-t hairline text-[11px] text-foreground/65 leading-snug inline-flex items-start gap-1.5">
              <Sparkles className="h-3 w-3 mt-0.5 text-foreground/65 shrink-0" />
              <span>Dining is on track to overshoot by ~$18 this month — based on the last 4 weeks.</span>
            </div>
          </div>
        </div>

        {/* Footer band */}
        <div className="border-t hairline bg-foreground/[0.02] px-5 py-2.5 flex items-center justify-between">
          <span className="text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3" />
            Bank connections · OAuth · read-only by default
          </span>
          <span className="font-mono text-[10px] text-foreground/65">3 accounts · synced 2 min ago</span>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/65">
        <ArrowUpRight className="h-3 w-3" />
        <span>Coming soon to <span className="font-mono text-foreground/65">bank.askfinz.ai</span></span>
      </div>
    </div>
  );
}
